import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiHelperService } from '../helpers/api-helper.service';
import { EnvService } from '../helpers/env.service';
import { ToastService } from './toast.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';

/** Allowed image MIME types for Quill editor uploads */
const VALID_IMAGE_TYPES = ['image/png', 'image/gif', 'image/jpeg', 'image/jpg', 'image/bmp', 'image/x-icon', 'image/webp'];

const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB

@Injectable({ providedIn: 'root' })
export class QuillImageUploadService {
    private readonly uploadPath = environment.FILE_UPLOAD_PATH;

    constructor(
        private api: ApiHelperService,
        private env: EnvService,
        private toast: ToastService,
        private translate: TranslateService,
    ) {}

    private get fileUrl(): string {
        return this.env.FILE_URL || '';
    }

    /**
     * Validates that the file is an allowed image type.
     */
    isValidImageFile(file: File): boolean {
        const type = (file.type || '').toLowerCase();
        return VALID_IMAGE_TYPES.some(t => type === t || type === t.replace('jpeg', 'jpg'));
    }

    private formatFileSize(bytes: number): string {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    }

    /**
     * Uploads an image file via FormData POST and returns the full public URL.
     * @param file - The image file to upload
     * @returns Promise resolving to the full image URL, or rejecting on error
     */
    async uploadImage(file: File): Promise<string> {
        if (!this.isValidImageFile(file)) {
            throw new Error('Invalid image file. Allowed: PNG, GIF, JPEG, BMP, ICO, WebP.');
        }

        if (file.size > MAX_IMAGE_SIZE) {
            const errorMsg = this.translate.instant('notifications.quillImage.fileTooLarge', {
                maxSize: this.formatFileSize(MAX_IMAGE_SIZE),
                currentSize: this.formatFileSize(file.size),
            });
            this.toast.error(errorMsg);
            throw new Error(errorMsg);
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('fileType', '');
        formData.append('visibility', 'PUBLIC');

        const res = await firstValueFrom(this.api.postFormData(this.uploadPath, formData));
        if (!res) {
            throw new Error('Upload failed.');
        }
        const body = res?.data ?? res;
        const fileName = body?.fileName;

        if (!fileName || typeof fileName !== 'string') {
            const msg = body?.message || res?.message || 'Upload failed.';
            this.toast.error(msg);
            throw new Error(msg);
        }

        const base = this.fileUrl || '';
        return base ? `${base}${fileName}` : fileName;
    }
}
